import { Platform } from 'react-native';

let OneSignal: any = null;
let LogLevel: any = null;

// Native-only SDK: the Platform guard lets Metro drop the module entirely from
// web bundles (Platform.OS is inlined in production web builds), so its ~55KB
// no longer downloads or executes on web. Every call site already checks the
// platform before using the service.
if (Platform.OS !== 'web') {
  try {
    const osModule = require('react-native-onesignal');
    OneSignal = osModule.OneSignal;
    LogLevel = osModule.LogLevel;
  } catch (error) {
    console.warn('[OneSignal] react-native-onesignal not available:', error);
  }
}

// OneSignal app "Hekaya" (org 83d5af70). The previous id (3e74a3be-...) was
// the "Drama Pills" app inherited from the cloned project: devices were
// registering their push subscriptions under the wrong OneSignal app, so
// pushes sent server-side (ONESIGNAL_APP_ID env = Hekaya) reached nobody.
const ONESIGNAL_APP_ID = 'e40a1427-118a-43ff-9b3a-732ccb5fb3f6';

export interface NotificationData {
  episodeId?: string;
  seriesId?: string;
  seasonId?: string;
  [key: string]: any;
}

export interface NotificationOpenedEvent {
  notification: {
    additionalData?: NotificationData;
    title?: string;
    body?: string;
  };
}

class OneSignalService {
  private isInitialized = false;
  private currentUserId: string | null = null;

  initialize(): void {
    if (!OneSignal) {
      console.log('[OneSignal] Not available on this platform, skipping...');
      return;
    }

    if (this.isInitialized) {
      console.log('[OneSignal] Already initialized, skipping...');
      return;
    }

    try {
      console.log('[OneSignal] Initializing OneSignal with app ID:', ONESIGNAL_APP_ID);

      OneSignal.initialize(ONESIGNAL_APP_ID);

      if (__DEV__) {
        OneSignal.Debug.setLogLevel(LogLevel.Verbose);
      }

      this.isInitialized = true;
      console.log('[OneSignal] Successfully initialized');
    } catch (error) {
      console.error('[OneSignal] Initialization error:', error);
    }
  }

  async requestPermission(): Promise<boolean> {
    if (!this.isInitialized) {
      console.warn('[OneSignal] Cannot request permission - not initialized');
      return false;
    }

    try {
      const currentPermission = await OneSignal.Notifications.getPermissionAsync();
      console.log('[OneSignal] Current permission status:', currentPermission);

      if (currentPermission) {
        console.log('[OneSignal] Permission already granted, skipping request');
        return true;
      }

      console.log('[OneSignal] ========================================');
      console.log('[OneSignal] REQUESTING NOTIFICATION PERMISSION');
      console.log('[OneSignal] Platform:', Platform.OS);
      console.log('[OneSignal] Initialized:', this.isInitialized);
      console.log('[OneSignal] ========================================');

      const permission = await OneSignal.Notifications.requestPermission(true);

      console.log('[OneSignal] ========================================');
      console.log('[OneSignal] PERMISSION REQUEST RESULT:', permission);
      console.log('[OneSignal] ========================================');

      if (permission) {
        const playerId = await this.getPlayerId();
        console.log('[OneSignal] Permission granted, player ID:', playerId);
      } else {
        console.log('[OneSignal] Permission denied by user');
      }

      return permission;
    } catch (error) {
      console.error('[OneSignal] ========================================');
      console.error('[OneSignal] PERMISSION REQUEST ERROR:', error);
      console.error('[OneSignal] ========================================');
      return false;
    }
  }

  async requestPermissionWithDelay(delayMs: number = 2000): Promise<boolean> {
    console.log(`[OneSignal] Waiting ${delayMs}ms before requesting permission...`);

    return new Promise((resolve) => {
      setTimeout(async () => {
        const result = await this.requestPermission();
        resolve(result);
      }, delayMs);
    });
  }

  async login(userId: string): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[OneSignal] Cannot login - not initialized');
      return;
    }

    try {
      console.log('[OneSignal] Logging in user:', userId);
      await OneSignal.login(userId);
      this.currentUserId = userId;
      console.log('[OneSignal] User logged in successfully');

      const playerId = await this.getPlayerId();
      if (playerId) {
        console.log('[OneSignal] Player ID after login:', playerId);
      }
    } catch (error) {
      console.error('[OneSignal] Login error:', error);
    }
  }

  async logout(): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[OneSignal] Cannot logout - not initialized');
      return;
    }

    try {
      console.log('[OneSignal] Logging out user');
      await OneSignal.logout();
      this.currentUserId = null;
      console.log('[OneSignal] User logged out successfully');
    } catch (error) {
      console.error('[OneSignal] Logout error:', error);
    }
  }

  async getPlayerId(): Promise<string | null> {
    if (!this.isInitialized) {
      console.warn('[OneSignal] Cannot get player ID - not initialized');
      return null;
    }

    try {
      const deviceState = await OneSignal.User.pushSubscription.getIdAsync();
      console.log('[OneSignal] Player ID:', deviceState);
      return deviceState || null;
    } catch (error) {
      console.error('[OneSignal] Error getting player ID:', error);
      return null;
    }
  }

  async hasPermission(): Promise<boolean> {
    if (!this.isInitialized) {
      console.warn('[OneSignal] Cannot check permission - not initialized');
      return false;
    }

    try {
      const hasPermission = await OneSignal.Notifications.getPermissionAsync();
      console.log('[OneSignal] Has permission:', hasPermission);
      return hasPermission;
    } catch (error) {
      console.error('[OneSignal] Error checking permission:', error);
      return false;
    }
  }

  setNotificationOpenedHandler(handler: (event: NotificationOpenedEvent) => void): void {
    if (!this.isInitialized) {
      console.warn('[OneSignal] Cannot set notification handler - not initialized');
      return;
    }

    console.log('[OneSignal] Setting notification opened handler');
    OneSignal.Notifications.addEventListener('click', (event: any) => {
      console.log('[OneSignal] Notification clicked:', JSON.stringify(event, null, 2));

      const notificationData = event?.notification?.additionalData || {};

      handler({
        notification: {
          additionalData: notificationData,
          title: event?.notification?.title,
          body: event?.notification?.body,
        },
      });
    });
  }

  setNotificationWillShowInForegroundHandler(handler: (event: any) => void): void {
    if (!this.isInitialized) {
      console.warn('[OneSignal] Cannot set foreground handler - not initialized');
      return;
    }

    console.log('[OneSignal] Setting foreground notification handler');
    OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event: any) => {
      console.log('[OneSignal] Notification will show in foreground:', JSON.stringify(event, null, 2));
      handler(event);
    });
  }

  async sendTag(key: string, value: string): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[OneSignal] Cannot send tag - not initialized');
      return;
    }

    try {
      console.log('[OneSignal] Sending tag:', key, '=', value);
      await OneSignal.User.addTag(key, value);
      console.log('[OneSignal] Tag sent successfully');
    } catch (error) {
      console.error('[OneSignal] Error sending tag:', error);
    }
  }

  async sendTags(tags: Record<string, string>): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[OneSignal] Cannot send tags - not initialized');
      return;
    }

    try {
      console.log('[OneSignal] Sending tags:', tags);
      await OneSignal.User.addTags(tags);
      console.log('[OneSignal] Tags sent successfully');
    } catch (error) {
      console.error('[OneSignal] Error sending tags:', error);
    }
  }

  getCurrentUserId(): string | null {
    return this.currentUserId;
  }

  isReady(): boolean {
    return this.isInitialized;
  }
}

export const oneSignalService = new OneSignalService();
